/**
 * deriveRowFormulas
 *
 * Materializes reactive formula outputs on a row proxy, computing through the
 * shared arTableFormulaEngine (computeArRowFormulas owns operand reads, skip
 * semantics and the non-finite→null rule). It performs NO Firestore write:
 * `arTableFormulaRowTrigger` / `reconcileArRow` is the sole persistent writer,
 * so browsing a table can no longer mutate stored data (SYD-2292).
 *
 * Every result — including null — lands on `formulaResults_<typeId>` at the
 * entry level, OUTSIDE `rowEntry.data`, so mergeDocumentIntoProxy's mirror-delete
 * cannot strip it while a server write is in flight. ArCell and the export paths
 * read that channel first; the keys hold `number | null`, never `undefined`, so
 * key presence and a `!== undefined` read are equivalent.
 *
 * Finite results are ALSO mirrored into the local `data.cells` proxy
 * (`cells[typeId].calcInput`, or `cells[col].value.fields[sub].value` for dotted
 * typeIds) — local only, never sent anywhere. That mirror is the display channel
 * for the surfaces still reading `data.cells`: the CompositeField sidebar,
 * clipboard copy and the client aggregation mirror. It is optimistic and the
 * next snapshot merge strips it until the trigger's own write lands; the plan's
 * step 4 converts those readers onto the engine, and this mirror goes with them.
 * A skipped row (per resolveArRowFormulaSkip) writes nothing at all.
 */

import { subscribe } from 'valtio';
import debounce from 'lodash/debounce';
import { computeArRowFormulas } from '#types';
import type { ArFormulaEngineConfig } from '#types';
import type { ArRowData, ArRowId } from '#types';
import type { DocEntry } from '../../Store.types';

const rowFormulasDerivationKey = Symbol('arTable.rowFormulasDerivation');
const formulaDebounceMs = 16;

/** DocEntry extended with ad-hoc derived properties written by this module. */
type RowEntryProxy = DocEntry<ArRowData> & Record<string | symbol, unknown>;

/**
 * Attaches formula derives to a row and subscribes for recomputation.
 *
 * @param rowId - The row document ID
 * @param rowEntry - The row's DocEntry proxy from the collection state
 * @param config - Shared formula engine config (sequence + bundlesEnabled)
 * @returns Cleanup function that removes derives and unsubscribes
 */
function deriveRowFormulas(
  rowId: ArRowId,
  rowEntry: DocEntry<ArRowData>,
  config: ArFormulaEngineConfig
): () => void {
  const proxy = rowEntry as RowEntryProxy;
  const existing = proxy[rowFormulasDerivationKey] as
    | { cleanup: () => void }
    | undefined;
  if (existing) return existing.cleanup;

  if (config.formulaSequence.length === 0) {
    return () => undefined;
  }

  let isApplying = false;

  const compute = (): void => {
    if (isApplying) return;
    isApplying = true;
    try {
      const computation = computeArRowFormulas(rowId, rowEntry.data, config);
      if (computation.skipped) return;

      const data = rowEntry.data as Record<string, unknown> | undefined;

      for (const { typeId } of config.formulaSequence) {
        const result = computation.results.get(typeId) ?? null;
        proxy[`formulaResults_${typeId}`] = result;

        if (typeof result !== 'number') continue;
        if (!data) continue;

        if (!('cells' in data) || !data.cells || typeof data.cells !== 'object') {
          data.cells = {};
        }
        const mutableCells = data.cells as Record<string, Record<string, unknown>>;

        const dotIndex = (typeId as string).indexOf('.');
        if (dotIndex === -1) {
          if (!mutableCells[typeId] || typeof mutableCells[typeId] !== 'object') {
            mutableCells[typeId] = {};
          }
          if (!Object.is(mutableCells[typeId].calcInput, result)) {
            mutableCells[typeId].calcInput = result;
          }
        } else {
          const col = (typeId as string).slice(0, dotIndex);
          const sub = (typeId as string).slice(dotIndex + 1);

          if (!mutableCells[col] || typeof mutableCells[col] !== 'object') {
            mutableCells[col] = {};
          }
          const cellProxy = mutableCells[col] as Record<string, unknown>;
          if (!cellProxy.value || typeof cellProxy.value !== 'object') {
            cellProxy.value = { fields: {} };
          }
          const valueProxy = cellProxy.value as Record<string, Record<string, unknown>>;
          if (!valueProxy.fields || typeof valueProxy.fields !== 'object') {
            valueProxy.fields = {};
          }
          const fieldsProxy = valueProxy.fields as Record<string, Record<string, unknown>>;
          if (!fieldsProxy[sub] || typeof fieldsProxy[sub] !== 'object') {
            fieldsProxy[sub] = {};
          }

          if (!Object.is(fieldsProxy[sub].value, result)) {
            fieldsProxy[sub].value = result;
          }
        }
      }
    } finally {
      isApplying = false;
    }
  };

  const debouncedCompute = debounce(compute, formulaDebounceMs, { leading: true, trailing: true });

  compute();

  const unsubscribe = subscribe(rowEntry.data, () => {
    if (isApplying) return;
    debouncedCompute();
  });

  const cleanup = (): void => {
    unsubscribe();
    debouncedCompute.cancel();

    delete proxy[rowFormulasDerivationKey];

    /** Deletions are deferred so they don't trigger subscriber state updates
     * when cleanup runs synchronously during the render phase (useArTableFormulas
     * attaches derives during render for subscription timing). Keys re-claimed
     * by a newer derivation are left in place. */
    const keysToDelete = config.formulaSequence.map(({ typeId }) => `formulaResults_${typeId}`);
    queueMicrotask(() => {
      const current = proxy[rowFormulasDerivationKey] as
        | { ownedKeys?: Set<string> } | undefined;
      const ownedByNew = current?.ownedKeys;
      for (const key of keysToDelete) {
        if (ownedByNew?.has(key)) continue;
        delete proxy[key];
      }
    });
  };

  const ownedKeys = new Set(config.formulaSequence.map(({ typeId }) => `formulaResults_${typeId}`));
  proxy[rowFormulasDerivationKey] = { cleanup, ownedKeys };

  return cleanup;
}

export default deriveRowFormulas;
